import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { ApiClient, OfflineError, type Session } from '@mads/shared'

const TOKEN_STORAGE_KEY = 'mads.session'

interface SessionState {
  token: string | null
  session: Session | null
  /** True until the stored token has been checked against the API. */
  loading: boolean
  /**
   * True once this page load has heard back from the API (issue #64). Never
   * persisted: a restored session is a copy of what the server last said, not
   * evidence that the token still works. It is what `useRestoreSession` reads
   * to decide whether to ask — a session in hand no longer answers that
   * question, because one now arrives from storage as well as from a sign-in.
   */
  verified: boolean
  setToken: (token: string | null) => void
  setSession: (session: Session | null) => void
  setLoading: (loading: boolean) => void
  /** Lands a session the API has just confirmed, and stops the splash screen. */
  sessionVerified: (session: Session) => void
  /** Lands a freshly signed-in token and session in one update. */
  signIn: (token: string, session: Session) => void
  signOut: () => void
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      token: null,
      session: null,
      loading: true,
      verified: false,
      setToken: (token) => set({ token }),
      setSession: (session) => set({ session }),
      setLoading: (loading) => set({ loading }),
      sessionVerified: (session) => set({ session, loading: false, verified: true }),
      // One atomic update so `useRestoreSession` sees a verified session: it
      // returns early and never sets `loading`, so signing in neither flashes
      // the splash screen nor fires a redundant `api.session()` — the sign-in
      // handler already fetched it.
      signIn: (token, session) => set({ token, session, loading: false, verified: true }),
      signOut: () => set({ token: null, session: null, loading: false, verified: false }),
    }),
    {
      name: TOKEN_STORAGE_KEY,
      // The session is stored beside the token so the app can open with no
      // network at all (issue #64) — a walk round a cellar is exactly where
      // there is no signal, and `api.session()` failing there must not read as
      // a rejected token.
      //
      // This does not make the stored copy authoritative: every page load still
      // asks the API and overwrites it, so a role change on the server takes
      // effect on the next load exactly as it did before. The stored copy is
      // the fallback for when there is nobody to ask.
      partialize: (state) => ({ token: state.token, session: state.session }),
      onRehydrateStorage: () => (state) => {
        // Nothing stored means there is nothing to verify — stop loading now.
        if (state !== undefined && state.token === null) state.setLoading(false)
      },
    },
  ),
)

/**
 * One client for the whole app. It reads the token straight from the store, so
 * it never goes stale, and signs out on any 401.
 */
export const api = new ApiClient({
  getToken: () => useSessionStore.getState().token,
  onUnauthorized: () => {
    useSessionStore.getState().signOut()
  },
})

/**
 * Finishes a sign-in for a token the app did not ask for — the one Google's
 * callback hands back in the URL fragment (issue #13).
 *
 * The token is verified with a throwaway client rather than by putting it in
 * the store first: a store holding a token but no session is the state
 * `useRestoreSession` treats as "restored from storage, go and check it", which
 * would show the splash screen and fetch the session a second time. Landing
 * both together, as the password path does, means neither happens — and a token
 * that turns out not to work never reaches the store at all.
 */
export async function completeSignIn(token: string): Promise<void> {
  const session = await new ApiClient({ getToken: () => token }).session()

  useSessionStore.getState().signIn(token, session)
}

/**
 * Checks a token restored from storage against the API, and lands the session
 * it answers with. Nothing renders behind the splash screen until this settles,
 * because the router reads the token in `beforeLoad`.
 *
 * Being unable to ask is not the same as being told no (issue #64). A tablet
 * opened in a cellar has a token, a stored session and no network; signing it
 * out there would strand the one job this app exists to do away from a desk. So
 * a connection failure keeps the session already in hand and leaves `verified`
 * false, which is what makes the caller ask again when the network returns.
 * Only the API actually refusing the token ends the session — that arrives as a
 * 401, and the client has already signed out by the time this sees it.
 *
 * The token is re-read before anything is written rather than the caller
 * holding a cancellation flag: what must not happen is a slow answer for one
 * account landing on top of another, and comparing the token is what actually
 * says that, whereas an unmount never happens in this app at all.
 */
export async function restoreSession(): Promise<void> {
  const { token, session, sessionVerified, setLoading, signOut } = useSessionStore.getState()

  // A stored session means there is something to show while the check runs, so
  // the splash screen is for the case where there is not.
  setLoading(session === null)

  try {
    const restored = await api.session()

    if (useSessionStore.getState().token === token) sessionVerified(restored)
  } catch (error) {
    if (useSessionStore.getState().token !== token) return

    if (error instanceof OfflineError && session !== null) {
      setLoading(false)
      return
    }

    signOut()
  }
}

export function useIsAdmin(): boolean {
  return useSessionStore((s) => s.session?.permissions.manageUsers ?? false)
}

export function useCurrentUser() {
  return useSessionStore((s) => s.session?.user ?? null)
}

/**
 * Whether this installation stores photographs of kit (issue #56). Deployment
 * configuration, not data — false leaves the upload control off the item and
 * fixture-model pages with the reason beside it.
 *
 * Defaults to false while the session is still loading, which is the right way
 * round: a control that appears late is better than one that appears and then
 * turns out not to work.
 */
export function useImagesConfigured(): boolean {
  return useSessionStore((s) => s.session?.imagesConfigured ?? false)
}
